import { OnDestroy, OnInit, ElementRef, Directive } from '@angular/core';
import { Observable, BehaviorSubject, fromEvent, Subscription } from 'rxjs';
import { map, switchMap, filter, throttleTime, takeWhile } from 'rxjs/operators';
import { Learnspace, Hub, ListQueryOptions } from '@shared/models';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { LearnspaceEditDialogComponent } from '@app/main/learnspaces/learnspace-edit-dialog/learnspace-edit-dialog.component';
import { LearnspaceWelcomeDialogComponent } from '@app/main/learnspaces/learnspace-welcome-dialog/learnspace-welcome-dialog.component';
import { CurrentHubService } from '@shared/services/currentHub.service';
import { courseLabel, spaceLabel, StringCase } from '@shared/utils/wording.utils';

@Directive()
export abstract class LearnspaceListBaseComponent implements OnInit, OnDestroy {
  hub: Hub;
  nbMySpaces: number;
  nbAll: number;
  lsList: Learnspace[];
  joinedList: Learnspace[];
  privateList: Learnspace[];
  refresh = new BehaviorSubject<string>(null);
  refreshObs = this.refresh.asObservable();
  roomUrl: string;
  url: string;
  isAdminOfHub = false;
  maxSpace: number;
  private _pageSize = 5;
  private _listOptions = new ListQueryOptions('status,-updatedAt', '', 0, this._pageSize, true);
  private _page: number;
  private _total: number;
  private _scrollSubs: Subscription;
  listingMode: string;
  protected _defaultListingMode: string = 'latest';
  // TODO: Sort direction (ASC, DESC) could be handled more carefully
  public sortOptions: { value: string; name: string; }[];
  public selectedSort = 'status,-updatedAt';

  constructor(
    protected _courseApiService: CoursesAPIService,
    protected _dialog: MatDialog,
    protected _router: Router,
    protected _elRef: ElementRef,
    protected _currentHub: CurrentHubService
  ) {
    this.roomUrl = environment.roomUrl;
  }

  ngOnInit(): void {
    this._currentHub.hubObservable.subscribe(hub => {
      if(hub) {
        this.hub = hub;
        this.maxSpace = this._currentHub.plan.maxSpace;
        this.countSpaces();
        this.setListMode(this._defaultListingMode);
      }

      this.sortOptions = [
        {
          value: 'title',
          name: `${spaceLabel(hub?.mode, false, StringCase.titlecase)} Title`
        },
        {
          value: 'status,-updatedAt',
          name: 'Last Modified'
        },
        {
          value: 'status,-experienceCount',
          name: `Number of ${courseLabel(hub?.mode, true, StringCase.titlecase)}`
        },
        {
          value: 'status,-participantCount',
          name: 'Number of Participants'
        }
      ];
    })
  }

  ngOnDestroy(): void {
    this.destroyScroll();
  }
  setListMode(change: string): void {
    this.listingMode = change;
    this.reloadList();
  }
  reloadList(): void {
    this.destroyScroll();
    this.lsList = [];
    this._page = 0;
    this._listOptions.offset = 0;
    if (this.listingMode !== 'latest') {
      this._listOptions.sort = this.selectedSort;
      this.getContent().subscribe(() => this.initScroll());
    } else {
      // Reset sort options
      this.selectedSort = 'status,-updatedAt';
      this._listOptions.sort = this.selectedSort;
      this.getMixedContent().subscribe(result => {
        this.joinedList = result.joined;
        this.privateList = result.privates;
      });
    }
  }
  sortList(): void {
    this._listOptions.sort = this.selectedSort;
    this.reloadList();
  }
  countSpaces(event?: any): void {
    if(event && event.shouldReloadList) {
      this.reloadList();
    } 
    this._courseApiService.countLearnspacesInHub(this.hub.id)
      .subscribe(result => {
        this.nbMySpaces = result.joined;
        this.nbAll = result.all;
      });
  }

  canCreateSpace(): boolean {
    return this.nbAll !== undefined && this.maxSpace > this.nbAll;
  }

  initScroll(): void {
    const scrolledElm = this._elRef.nativeElement;
    this._scrollSubs = fromEvent(scrolledElm, 'scroll')
      .pipe(
        throttleTime(100),
        takeWhile(() => this._total > this.lsList.length),
    ).subscribe(() => {
      const leftToScroll = scrolledElm.scrollHeight - scrolledElm.clientHeight - scrolledElm.scrollTop; // at bottom clientHeight + scrollTop = scrollHeight
      const threshold = scrolledElm.scrollHeight * 0.2; // 20% should be computed once
      if (leftToScroll < threshold) {
        this.getMoreContent();
      }
    });
  }

  destroyScroll(): void {
    if (this._scrollSubs) {
      this._scrollSubs.unsubscribe();
    }
  }

  getMixedContent(): Observable< { joined: Learnspace[], privates: Learnspace[] }> {
    return this._courseApiService.getLearnspacesJoinedByHub(this.hub.id, this._listOptions)
      .pipe(
        switchMap(joinedSpaces => {
          const customListOptions = { ...this._listOptions, limit: 20 }; // TODO find better way to have space i am not participant
          return this._courseApiService.getLearnspacesByHub(this.hub.id, customListOptions)
            .pipe(
              map(spaces => {
                const privateSpaces = spaces.learnspaces.filter(l => !l.isParticipating);
                return {
                  joined: joinedSpaces.learnspaces,
                  privates: privateSpaces.slice(0, 5)
                };
              })
            );
        })
      );
  }

  getContent(): Observable< { total?: number, learnspaces: Learnspace[] }> {
    let request;
    if (this.listingMode === 'joined') {
      request = this._courseApiService.getLearnspacesJoinedByHub(this.hub.id, this._listOptions);
    } else if (this.listingMode === 'allPrivate') {
      request = this._courseApiService.getLearnspacesByHub(this.hub.id, this._listOptions);
    }

    return request.pipe(
      map(response => {
        this._total = response.total;
        this.lsList = this.lsList.concat(response.learnspaces);
        return response;
      })
    );
  }

  getMoreContent(): void {
    this._page += 1;
    this._listOptions.offset = this._pageSize * this._page;
    this.getContent().subscribe();
  }

  addLearnspace(): void {
    this._dialog
      .open(
        LearnspaceEditDialogComponent,
        {
          width: '50vw',
          data: { hubId: this.hub.id, mode: this.hub.mode }
        }
      )
      .afterClosed()
      .pipe(
        filter(a => a),
        map(ls => this._router.navigate([this.hub.urlTitle, ls.id]))
      ).subscribe();
  }

  displayWelcomeMessage(): void {
    this._dialog
      .open(LearnspaceWelcomeDialogComponent, { width: '45vw', height: '70vh' });
  }
}
